import { statusFormProps } from "../shared/statusFormProps";
import Stack from "@mui/material/Stack";
import { useTranslation } from "@privacy/translations";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Drawer } from "../shared/Drawer";
import { DynamicForm } from "../dynamicForm";
import { DynamicGrid } from "../dynamicGrid";
import { usePermission } from "../shared/permissions";
import { TenantIcon2 } from "../shared/icons";
import { usePrivacyNavigate } from "../shared/usePrivacyNavigate";
import {
  setSharedProcessingResponsibleMutationOptions,
  sharedProcessingByIdQueryOptions,
  sharedProcessingFormQueryOptions,
  sharedProcessingSharedOrganisationsQueryOptions,
  updateSharedProcessingAnswersMutationOptions,
  updateSharedProcessingStatusMutationOptions,
} from "../api/sharedProcessings";
import { DatesBand } from "../shared/DateTimeInfo";
import { DescriptionStatusSelect } from "./DescriptionStatusSelect";
import { SharedProcessingDrawerHeader } from "./SharedProcessingDrawerHeader";
import { SharedProcessingResponsible } from "./SharedProcessingResponsible";

export const SharedProcessingInlineFormDrawer = () => {
  const sharedProcessingId = DynamicGrid.useSelectedRowId();
  const translation = useTranslation();
  const { t } = translation;
  const sharedProcessingQuery = useQuery(sharedProcessingByIdQueryOptions(sharedProcessingId));
  const updateStatusMutation = useMutation(updateSharedProcessingStatusMutationOptions(sharedProcessingId));
  const formQuery = useQuery(sharedProcessingFormQueryOptions());
  const answersMutation = useMutation(updateSharedProcessingAnswersMutationOptions());
  const setResponsibleMutation = useMutation(setSharedProcessingResponsibleMutationOptions());
  const sharedOrganisationsQuery = useQuery(sharedProcessingSharedOrganisationsQueryOptions(sharedProcessingId));
  const { hasPermission } = usePermission();
  const { navigate } = usePrivacyNavigate();
  const isReadOnly = !hasPermission("nova-forms-api:enterprise-customers:registrations:manage");

  return (
    <DynamicGrid.Drawer>
      {(() => {
        if (!sharedProcessingQuery.data || !formQuery.data) return <Drawer.Loading />;

        const sharedProcessing = sharedProcessingQuery.data;
        return (
          <DynamicForm.Provider
            key={sharedProcessing.id}
            fileOwner={{ type: "Registration", id: sharedProcessing.id }}
            onSave={answer => answersMutation.mutateAsync({ id: sharedProcessing.id, answers: [answer] })}
            answers={sharedProcessing.answers}
            isReadOnly={isReadOnly}
            isSaveError={answersMutation.isError || setResponsibleMutation.isError}
            isSaving={answersMutation.isPending || setResponsibleMutation.isPending}
            {...statusFormProps(
              sharedProcessing,
              updateStatusMutation.error,
              t("RegistrationDrawer.UnansweredMandatoryQuestion.Input")
            )}
          >
            {({ formState }) => (
              <>
                <SharedProcessingDrawerHeader
                  activeTab="standard"
                  sharedProcessing={sharedProcessing}
                  statusIndicator={DynamicForm.formStateToDrawerHeaderStatus(formState)}
                  bottomSlot={
                    <DatesBand
                      createdAt={sharedProcessing.createdAt}
                      updatedAt={sharedProcessing.updatedAt}
                      action={
                        <DescriptionStatusSelect
                          sharedProcessing={sharedProcessing}
                          updateStatusMutation={updateStatusMutation}
                        />
                      }
                    />
                  }
                />
                <Drawer.Content sx={{ pt: 3 }}>
                  <Stack sx={{ mb: 5 }}>
                    <SharedProcessingResponsible
                      isReadOnly={isReadOnly}
                      sharedProcessing={sharedProcessing}
                      setResponsibleMutation={setResponsibleMutation}
                    />
                    {hasPermission("nova-forms-api:enterprise-customers:registrations:manage") && (
                      <Drawer.GoToSubDrawerButtonsWrapper>
                        <Drawer.GoToSubDrawerButton
                          Icon={TenantIcon2}
                          label={t("SharedProcessing.PresentIn.Header")}
                          count={sharedOrganisationsQuery.data?.filter(organisation => organisation.isShared).length}
                          onClick={() => navigate("share")}
                        />
                      </Drawer.GoToSubDrawerButtonsWrapper>
                    )}
                  </Stack>

                  <Drawer.Divider />
                  <Stack sx={{ my: 5 }}>
                    <DynamicForm.InlineForm id={sharedProcessing.id} form={formQuery.data} />
                  </Stack>
                </Drawer.Content>
              </>
            )}
          </DynamicForm.Provider>
        );
      })()}
    </DynamicGrid.Drawer>
  );
};
